import * as React from 'react';
import { observer } from 'mobx-react';
import { Modal, ModalChildStyle } from '../modal';
import { style } from 'typestyle';
import { px } from 'csx';
import overlayStyles, { ScrollableOverlayCloseButtonStyle } from '../../styles/scrollable-overlay';
import { ChannelSchema } from '../../types/schema';
import GenericError from '../generic-error';
import { colorWhite, rebrand } from '../../theme/color';
import { Loading } from '../loading/loading';
import { ButtonProminence } from '../project-happy/atoms/button/buttonStyles';
import { CircleButton } from '../project-happy/atoms/button/CircleButton';
import { CloseCross } from '../project-happy/atoms/icons/CloseCross';
import { spacing } from '../../theme/spacing';
import { ScrollableOverlay } from '../scrollable-overlay/scrollable-overlay';
import { ChannelCard } from '../project-happy/molecules/ChannelCard';
import { shadowElevation5 } from '../../theme/shadow';
import { GoogleButton } from '../user/google-button';

export interface ChannelSelectModalV2Model {
  channels: Array<ChannelSchema>;
  channel: ChannelSchema;
  activating: ChannelSchema;
  show: boolean;
  loading: boolean;
  selecting: boolean;
  addUrl: string;
}

export interface ChannelSelectModalV2Props {
  model: ChannelSelectModalV2Model;
  onRequestClose(): void;
}

@observer
export class ChannelSelectModalV2 extends React.Component<ChannelSelectModalV2Props, any> {
  render() {
    const { onRequestClose, model } = this.props;
    const { loading, selecting, addUrl, show } = model;

    const header = (
      <header className={ChannelSelectModalV2.styles.header}>
        <h3>Change channel</h3>
        <CircleButton
          onClick={onRequestClose}
          className={overlayStyles.closeButton}
          prominence={ButtonProminence.SECONDARY}
          aria-label="Close modal"
          data-test-close-modal
        >
          <CloseCross />
        </CircleButton>
      </header>
    );

    if (loading)
      return (
        <Modal show={show} onRequestClose={onRequestClose}>
          <section className={ChannelSelectModalV2.styles.container}>
            {header}
            <Loading text="Loading channels..." />
          </section>
        </Modal>
      );

    return (
      <ScrollableOverlay
        show={show}
        onRequestClose={selecting ? void 0 : onRequestClose}
        className={ChannelSelectModalV2.styles.wrapper}
        data-test-channel-select-modal
      >
        <div className={overlayStyles.header}>
          <h3 className={(overlayStyles.title, ChannelSelectModalV2.styles.largeFont)} id="change-channel-title">
            Change channel
          </h3>
        </div>
        <div className={ChannelSelectModalV2.styles.channelCardContainer}>
          {!loading && model.channels.length > 0 && (
            <>
              {model.channels.map((channel) => (
                <ChannelCard channel={channel} key={channel.id} className={ChannelSelectModalV2.styles.shadow} />
              ))}
            </>
          )}

          {!loading && !model.channels.length && <GenericError />}
          <div className={ChannelSelectModalV2.styles.googleButtonContainer}>
            <p className={ChannelSelectModalV2.styles.addChannelText}>Need to add another channel?</p>
            <GoogleButton href={addUrl} data-test-continue-with-button-google />
          </div>
        </div>
      </ScrollableOverlay>
    );
  }

  static styles = {
    container: style({
      ...ModalChildStyle,
      width: px(288),
      padding: 0,
      backgroundColor: colorWhite.toHexString(),
    }),
    wrapper: style({ display: 'block', maxWidth: px(450), backgroundColor: rebrand.neutralOnDark[10].toString() }),
    header: style({
      position: 'relative',
      textAlign: 'center',
      $nest: {
        '& h3': {
          fontSize: px(32),
          margin: `${spacing.DEFAULT} 0`,
        },
      },
    }),
    button: style(ScrollableOverlayCloseButtonStyle),
    addChannelText: style({
      fontWeight: 700,
      fontSize: px(20),
      textAlign: 'center',
      marginBottom: px(8),
    }),
    googleButtonContainer: style({
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      flexDirection: 'column',
      width: '100%',
      margin: '32px 0px 8px',
    }),
    channelCardContainer: style({
      maxWidth: px(320),
      margin: 'auto',
    }),
    largeFont: style({
      fontSize: px(32),
    }),
    shadow: style({ ...shadowElevation5 }),
  };
}
